import React, { useState, useEffect, useMemo } from "react";
import { Option, Select, OnChangeParams } from "baseui/select";
import { useEstatesWithGroups } from "../../../hooks/useEstates";
import { useBusinesses } from "../../../hooks/useBusinessAccessType";
import { useGetBuilding, useGetEstateGroup } from "../../../api/generated";
import { Flex } from "../../../components/Flex";
import { useT } from "../../../i18n/admin";

export type SelectOptions = Option;

export type EstateGroupFirstOption = {
  estate_group_id: SelectOptions[];
  estate_id: SelectOptions[];
};

type Props = {
  defaultValue?: SelectOptions[];
  onChange?: (value: SelectOptions[]) => void;
  includeCurrentBusiness?: boolean;
  currentBusinessName?: string;
  currentBusinessId?: string;
  kind?: 'business' | 'residential';
  /** When set, only show estate groups/estates whose estate_id is in this list (property manager scope) */
  allowedEstateIds?: number[];
};

export const EstateGroupFirstControl = ({
  defaultValue,
  onChange,
  includeCurrentBusiness = false,
  currentBusinessName = '',
  currentBusinessId = '',
  kind = 'residential',
  allowedEstateIds,
}: Props) => {
  // For business, use the hook
  const { data: businessResponse, isLoading: businessLoading, error: businessError } = useBusinesses();
  
  const businessOptions = useMemo(() => {
    
    if (kind !== 'business') {
      return [];
    }

    // The API returns { data: Business[], total_pages, page, total_elements }
    // React Query stores this in businessResponse, so businessResponse.data is the array
    let businesses: any[] = [];

    if (Array.isArray(businessResponse)) {
      // If businessResponse is directly an array
      businesses = businessResponse;
    } else if (businessResponse?.data && Array.isArray(businessResponse.data)) {
      // If businessResponse has a data property that is an array
      businesses = businessResponse.data;
    } else if (businessResponse && typeof businessResponse === 'object') {
      // Log the structure to debug
    }
    
    if (businesses.length > 0) {
      const options = businesses.map((business: any) => ({
        label: business.name,
        id: business.id,
        isBusiness: true
      })).sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));
      return options;
    }
    
    return [];
  }, [businessResponse, kind, businessLoading]);

  // For residential, use current logic
  const { data: estatesResponse, isLoading, error } = useEstatesWithGroups();
  const { t } = useT();
  const [selectedEstateGroup, setSelectedEstateGroup] = useState<SelectOptions[]>([]);
  const [selectedBuilding, setSelectedBuilding] = useState<SelectOptions[]>([]);
  const [selectedEstate, setSelectedEstate] = useState<SelectOptions[]>(defaultValue || []);

  // Buildings that belong to the selected estate group. Same pattern as
  // AddUserEstateDrawer.tsx — a group without buildings simply returns an
  // empty list, which we use to fall back to the original two-level flow.
  const selectedGroupId = kind === 'residential' && selectedEstateGroup[0]?.id
    ? Number(selectedEstateGroup[0].id)
    : undefined;
  const { data: buildingsResponse, isLoading: buildingsLoading } = useGetBuilding(
    {
      limit: 200,
      estate_group_id: selectedGroupId,
      order_by: 'name',
      order_direction: 'ASC',
    } as any,
    { query: { enabled: kind === 'residential' && !!selectedGroupId } } as any,
  );
  const buildingOptions = useMemo(() => {
    if (kind !== 'residential' || !selectedGroupId) return [];
    const raw = buildingsResponse?.data as any;
    const buildings: any[] = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
    return buildings
      .map((b: any) => ({ label: b.name, id: b.id }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));
  }, [buildingsResponse, kind, selectedGroupId]);
  const groupHasBuildings = buildingOptions.length > 0;

  // SAM-1323: a group with no estate yet (AA Test) is still a group of the tenant, so the list also reads the
  // groups themselves. Not for a property manager: its groups are the ones of its estates.
  const { data: groupsResponse } = useGetEstateGroup(
    { page: 1, limit: 500 } as any,
    { query: { enabled: kind === 'residential' && !allowedEstateIds } } as any,
  );
  const tenantGroups: any[] = useMemo(() => {
    const raw = groupsResponse as any;
    const rows = raw?.data?.data ?? raw?.data ?? (Array.isArray(raw) ? raw : []);
    return Array.isArray(rows) ? rows : [];
  }, [groupsResponse]);

  // Extract unique estate groups from estates data, optionally scoped to allowedEstateIds
  const estateGroupOptions = useMemo(() => {
    if (kind !== 'residential') return [];
    const raw = estatesResponse as any;
    if (!raw) return [];
    const response: any[] = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
    const uniqueGroups = new Map();
    if (!allowedEstateIds) tenantGroups.forEach((g: any) => { if (g?.id != null && g.name) uniqueGroups.set(g.id, { label: g.name, id: g.id }); });
    response.forEach((estate: any) => {
      if (estate.estate_group) {
        // If allowedEstateIds is set, only include groups that contain at least one allowed estate
        if (allowedEstateIds && !allowedEstateIds.includes(estate.id)) return;
        uniqueGroups.set(estate.estate_group.id, {
          label: estate.estate_group.name,
          id: estate.estate_group.id,
        });
      }
    });
    return Array.from(uniqueGroups.values()).sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));
  }, [estatesResponse, kind, allowedEstateIds, tenantGroups]);

  // Filter estates by selected estate group and format for display, optionally scoped to allowedEstateIds
  const estateOptions = useMemo(() => {
    if (kind !== 'residential') return [];
    const raw = estatesResponse as any;
    if (!raw) return [];
    const estatesArray: any[] = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
    let filteredEstates = estatesArray;
    if (allowedEstateIds) {
      filteredEstates = filteredEstates.filter((estate: any) => allowedEstateIds.includes(estate.id));
    }
    if (selectedEstateGroup.length > 0) {
      const groupId = selectedEstateGroup[0].id;
      filteredEstates = filteredEstates.filter((estate: any) => estate.estate_group_id === groupId);
    }
    if (groupHasBuildings && selectedBuilding.length > 0) {
      const buildingId = selectedBuilding[0].id;
      filteredEstates = filteredEstates.filter((estate: any) => estate.building_id === buildingId);
    }
    return filteredEstates.map((estate: any) => ({
      label: estate.name,
      id: estate.id,
      estate_group_id: estate.estate_group_id,
      estate_group_name: estate.estate_group?.name,
      estate_name: estate.name,
      isBusiness: false
    })).sort((a: any, b: any) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));
  }, [estatesResponse, selectedEstateGroup, selectedBuilding, groupHasBuildings, kind, allowedEstateIds]);

  // Handle estate group changes
  const handleEstateGroupChange = (params: OnChangeParams) => {
    setSelectedEstateGroup(params.value as SelectOptions[]);
    setSelectedBuilding([]);
    setSelectedEstate([]);
    onChange?.([]);
  };

  // Handle building changes (only relevant when the selected group has buildings)
  const handleBuildingChange = (params: OnChangeParams) => {
    setSelectedBuilding(params.value as SelectOptions[]);
    setSelectedEstate([]);
    onChange?.([]);
  };

  // Handle estate changes
  const handleEstateChange = (params: OnChangeParams) => {
    const newValue = params.value as SelectOptions[];
    setSelectedEstate(newValue);
    onChange?.(newValue);
  };

  // Set initial estate group based on defaultValue
  useEffect(() => {
    
    if (kind === 'business') {
      // For business mode, only use defaultValue if provided
      if (defaultValue && defaultValue.length > 0) {
        setSelectedEstate(defaultValue);
      }
    } else if (kind === 'residential') {
      if (defaultValue && defaultValue.length > 0) {
        const selectedEstateData = defaultValue[0] as any;
        if (selectedEstateData?.isBusiness) {
          setSelectedEstateGroup([]);
          setSelectedBuilding([]);
          setSelectedEstate(defaultValue);
          return;
        }
        const raw = estatesResponse as any;
        const response: any[] = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
        if (response.length > 0) {
          const estate = response.find((e: any) => e.id === selectedEstateData.id);
          if (estate?.estate_group) {
            setSelectedEstateGroup([{
              label: estate.estate_group.name,
              id: estate.estate_group.id,
            }]);
          }
          // Building is filled in once its options load (see effect below),
          // since it depends on a separate request scoped to the estate group.
        }
        setSelectedEstate(defaultValue);
      }
    }
  }, [defaultValue, estatesResponse, kind, currentBusinessName, currentBusinessId]);

  // Once buildings for the restored estate group load, prefill the building
  // select if the previously chosen estate belongs to one of them.
  useEffect(() => {
    if (kind !== 'residential' || !groupHasBuildings) return;
    const currentEstateId = selectedEstate[0]?.id;
    if (currentEstateId == null || selectedBuilding.length > 0) return;
    const raw = estatesResponse as any;
    const response: any[] = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
    const estate = response.find((e: any) => e.id === currentEstateId);
    if (estate?.building_id != null) {
      const building = buildingOptions.find((b) => b.id === estate.building_id);
      if (building) setSelectedBuilding([building]);
    }
  }, [buildingOptions, groupHasBuildings, estatesResponse, kind, selectedEstate, selectedBuilding]);

  // Reset state when kind changes
  useEffect(() => {
    setSelectedEstateGroup([]);
    setSelectedBuilding([]);
    setSelectedEstate([]);
  }, [kind]);

  // Render for business
  if (kind === 'business') {
    return (
      <Flex flexDirection="column" gap="1rem">
        <Select
          value={selectedEstate}
          options={businessOptions}
          onChange={handleEstateChange}
          disabled={businessOptions.length === 0 || !!businessError || businessLoading}
          placeholder={
            businessLoading
              ? t("booking.destination.loadingBusinesses")
              : businessError
              ? t("booking.destination.errorBusinesses")
              : t("booking.destination.selectBusiness")
          }
          clearable={false}
          searchable={true}
          overrides={{
            Popover: {
              props: {
                overrides: {
                  Body: {
                    style: {
                      zIndex: '999999 !important',
                    },
                  },
                },
              },
            },
            Dropdown: {
              style: {
                zIndex: '999999 !important',
              },
            },
          }}
        />
      </Flex>
    );
  }

  // Render for residential (default)
  return (
    <Flex flexDirection="column" gap="1rem">
      <Select
        value={selectedEstateGroup}
        options={estateGroupOptions}
        onChange={handleEstateGroupChange}
        disabled={isLoading || !!error}
        placeholder={
          isLoading 
            ? t("booking.destination.loadingGroups") 
            : error 
            ? t("booking.destination.errorGroups") 
            : t("booking.destination.selectGroup")
        }
        clearable={false}
        searchable={false}
        overrides={{
          Popover: {
            props: {
              overrides: {
                Body: {
                  style: {
                    zIndex: '999999 !important',
                  },
                },
              },
            },
          },
          Dropdown: {
            style: {
              zIndex: '999999 !important',
            },
          },
        }}
      />
      {selectedEstateGroup.length > 0 && groupHasBuildings && (
        <Select
          value={selectedBuilding}
          options={buildingOptions}
          onChange={handleBuildingChange}
          disabled={buildingsLoading}
          placeholder={buildingsLoading ? t("booking.destination.loadingBuildings") : t("booking.destination.allBuildings")}
          clearable={true}
          searchable={true}
          overrides={{
            Popover: {
              props: {
                overrides: {
                  Body: {
                    style: {
                      zIndex: '999999 !important',
                    },
                  },
                },
              },
            },
            Dropdown: {
              style: {
                zIndex: '999999 !important',
              },
            },
          }}
        />
      )}
      <Select
        value={selectedEstate}
        options={estateOptions}
        onChange={handleEstateChange}
        disabled={isLoading || !!error || (selectedEstateGroup.length === 0 && !includeCurrentBusiness)}
        placeholder={
          selectedEstateGroup.length === 0 && !includeCurrentBusiness
            ? t("booking.destination.pickGroupFirst")
            : isLoading
            ? t("booking.destination.loadingEstates")
            : selectedEstateGroup.length > 0 && estateOptions.length === 0 && !groupHasBuildings && !buildingsLoading
            ? t("booking.destination.noEstates")
            : error
            ? t("booking.destination.errorEstates")
            : t("booking.destination.selectEstate")
        }
        clearable={false}
        searchable={true}
        overrides={{
          Popover: {
            props: {
              overrides: {
                Body: {
                  style: {
                    zIndex: '999999 !important',
                  },
                },
              },
            },
          },
          Dropdown: {
            style: {
              zIndex: '999999 !important',
            },
          },
        }}
      />
    </Flex>
  );
}; 